<script setup lang="ts">
/**
 * HousingContactActivityAssociations
 *
 * Custom form element for the Housing project that displays and manages entity
 * associations on a Contact Activity workflow or an On Hold workflow.
 *
 * Layout: three columns — Applications, Contacts, Organizations.
 *
 * Data flow (2 API calls):
 *  1. getLinkedEntities(workflowGuid)
 *     → finds the HousingApplication linked to THIS workflow
 *     → determines which contacts are already checked (linked to THIS workflow)
 *     → If no linked HousingApplication is found but the workflow's primary
 *       entity IS a housing application (e.g. On Hold workflows), it is used
 *       directly.
 *  2. getLinkedPeople(applicationEntityGuid, "Entity")
 *     → returns ALL people related to the housing application
 *     → this is the full checkbox list
 *
 * Checkbox toggles call linkEntityToWorkflow / unlinkEntityFromWorkflow to
 * persist associations through the existing workflow link system — nothing is
 * stored in FormAnswer tables.
 *
 * NOTE: GetRelatedEntitiesForAssociation was considered but it traverses
 * through the workflow's *primary entity* (the person). We need to traverse
 * through a *linked entity* (the housing application), so it does not fit.
 */
import type { CheckboxValueType } from "element-plus";
import { computed, ref, watch } from "vue";
import type { LinkedEntityDto, Workflow } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

/** Normalised contact for the checkbox list. */
interface ContactItem {
  entityGuid: string;
  name: string;
  isPrimary: boolean;
}

const props = withDefaults(
  defineProps<{
    /** Workflow of the Contact Activity workflow instance. */
    workflow?: Workflow;
    /** Entity GUID of the workflow's primary contact (rendered with a "Primary" badge). */
    primaryContactGuid?: string;
    /** Display name for the primary contact. */
    primaryContactName?: string;
    /** When true, all checkboxes are disabled. */
    readOnly?: boolean;
    /** The linkType value used when linking/unlinking contacts. */
    contactLinkType?: string;
  }>(),
  {
    workflow: undefined,
    primaryContactGuid: undefined,
    primaryContactName: undefined,
    readOnly: false,
    contactLinkType: "Additional Contact",
  },
);

// ---------------------------------------------------------------------------
// Reactive state
// ---------------------------------------------------------------------------

const loading = ref(false);
const loadError = ref<string | null>(null);

/** The HousingApplication entity linked to this workflow (shown in the Applications column). */
const application = ref<LinkedEntityDto | null>(null);

/** Full list of contacts to render checkboxes for. */
const contacts = ref<ContactItem[]>([]);

/** Entity GUIDs of contacts currently linked to THIS workflow (checked checkboxes). */
const linkedContactGuids = ref<Set<string>>(new Set());

/** Entity GUIDs of contacts whose link/unlink request is in‑flight (disabled while waiting). */
const inFlightGuids = ref<Set<string>>(new Set());

const hasWorkflow = computed(() => !!props.workflow);
const workflowGuid = computed(() => props.workflow?.guid);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Clear all loaded data (used when workflow becomes undefined). */
const resetState = () => {
  application.value = null;
  contacts.value = [];
  linkedContactGuids.value = new Set();
};

/**
 * Ensure the primary contact appears in the contact map with isPrimary = true.
 * If it already exists (from the linked-people query), promote it; otherwise
 * insert it so the UI always shows the primary contact row.
 */
const mergePrimaryContact = (contactMap: Map<string, ContactItem>) => {
  if (!props.primaryContactGuid) return;

  const existing = contactMap.get(props.primaryContactGuid);
  if (existing) {
    existing.isPrimary = true;
    if (props.primaryContactName && existing.name === "Unnamed Contact") {
      existing.name = props.primaryContactName;
    }
    return;
  }

  contactMap.set(props.primaryContactGuid, {
    entityGuid: props.primaryContactGuid,
    name: props.primaryContactName ?? "Primary Contact",
    isPrimary: true,
  });
};

/**
 * Build the set of entity GUIDs that are currently linked to THIS workflow
 * as contacts (matching contactLinkType). The primary contact is always
 * considered linked.
 */
const buildLinkedContactSet = (linkedEntities: LinkedEntityDto[]) => {
  const next = new Set<string>();
  for (const entity of linkedEntities) {
    if (entity.linkType === props.contactLinkType) {
      next.add(entity.entityGuid);
    }
  }
  if (props.primaryContactGuid) {
    next.add(props.primaryContactGuid);
  }
  linkedContactGuids.value = next;
};

// ---------------------------------------------------------------------------
// Load associations
// ---------------------------------------------------------------------------

/**
 * Main data-loading function. Makes two API calls:
 *
 *  1. getLinkedEntities — entities linked to THIS workflow.
 *     • Extracts the HousingApplication entity (Applications column).
 *     • Extracts which contacts are checked (contact GUIDs with contactLinkType).
 *
 *  2. getLinkedPeople — all Person entities linked to any workflow whose
 *     primary entity is the application entity.
 *     • This is the complete contact list for the Contacts column.
 *     • Uses guidType="Entity" because we have the application's entity GUID,
 *       not the housing-application table GUID.
 */
const loadAssociations = async () => {
  if (!props.workflow) {
    resetState();
    return;
  }

  loading.value = true;
  loadError.value = null;

  try {
    // Step 1 — linked entities for the current workflow
    const linkedEntities = props.workflow.linkedEntities
      ? await apiClient.workflowInstance.getLinkedEntities(props.workflow.guid)
      : [];

    // Find the HousingApplication entity (displayed in Applications column)
    // First, look in linked entities (Contact Activity workflow pattern)
    const linkedApp =
      linkedEntities.find(
        (entity) => entity.linkType === "HousingApplication",
      ) ?? null;

    if (linkedApp) {
      application.value = linkedApp;
    } else if (
      props.workflow?.entity?.entityDefinition?.name ===
        "Housing Application" ||
      props.workflow?.entity?.entityDefinition?.namePlural ===
        "Housing Applications"
    ) {
      // On Hold workflow: the primary entity IS the housing application.
      // Build a LinkedEntityDto-compatible object from the workflow entity.
      const entity = props.workflow.entity;
      application.value = {
        entityGuid: entity.guid,
        entityName: entity.name,
        entityDefinitionName:
          entity.entityDefinition?.name ?? "Housing Application",
        entityDefinitionGuid: entity.entityDefinitionGuid ?? "",
        linkType: "HousingApplication",
        createdAt: "",
      };
    } else {
      application.value = null;
    }

    // Determine which contacts are already checked
    buildLinkedContactSet(linkedEntities);

    // Step 2 — full contact list from the housing application's entity GUID
    const contactMap = new Map<string, ContactItem>();

    if (application.value?.entityGuid) {
      const linkedPeople = await apiClient.housingApplication.getLinkedPeople(
        application.value.entityGuid,
        "Entity",
      );

      for (const person of linkedPeople) {
        contactMap.set(person.entityGuid, {
          entityGuid: person.entityGuid,
          name:
            person.name && person.name.trim().length > 0
              ? person.name
              : "Unnamed Contact",
          isPrimary: false,
        });
      }
    }

    // Always show the primary contact, even if it wasn't in the linked-people result
    mergePrimaryContact(contactMap);

    // Sort: primary first, then alphabetically
    contacts.value = Array.from(contactMap.values()).sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  } catch (error) {
    loadError.value = "Unable to load associations.";
    showError(error);
  } finally {
    loading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Checkbox state
// ---------------------------------------------------------------------------

/** Whether the contact's checkbox should be checked. */
const isContactChecked = (contact: ContactItem) =>
  linkedContactGuids.value.has(contact.entityGuid);

/** Whether the contact's checkbox should be disabled (read‑only, primary, or in‑flight). */
const isContactDisabled = (contact: ContactItem) =>
  props.readOnly ||
  contact.isPrimary ||
  inFlightGuids.value.has(contact.entityGuid);

// ---------------------------------------------------------------------------
// Optimistic updates + API calls
// ---------------------------------------------------------------------------

/** Mark/unmark a contact GUID as having an in‑flight request. */
const updateInFlight = (contactGuid: string, isLoading: boolean) => {
  const next = new Set(inFlightGuids.value);
  if (isLoading) {
    next.add(contactGuid);
  } else {
    next.delete(contactGuid);
  }
  inFlightGuids.value = next;
};

/** Optimistically update the checked set after a successful link/unlink. */
const updateLinkedContacts = (contactGuid: string, checked: boolean) => {
  const next = new Set(linkedContactGuids.value);
  if (checked) {
    next.add(contactGuid);
  } else {
    next.delete(contactGuid);
  }
  linkedContactGuids.value = next;
};

/**
 * Link or unlink a contact entity to/from the current workflow.
 * Uses optimistic update on success; reloads everything on failure.
 */
const toggleContact = async (contact: ContactItem, checked: boolean) => {
  if (!workflowGuid.value || isContactDisabled(contact)) return;

  updateInFlight(contact.entityGuid, true);

  try {
    if (checked) {
      await apiClient.workflowInstance.linkEntityToWorkflow(
        workflowGuid.value,
        {
          entityGuid: contact.entityGuid,
          linkType: props.contactLinkType,
        },
      );
    } else {
      await apiClient.workflowInstance.unlinkEntityFromWorkflow(
        workflowGuid.value,
        contact.entityGuid,
      );
    }

    updateLinkedContacts(contact.entityGuid, checked);
  } catch (error) {
    showError(error);
    await loadAssociations();
  } finally {
    updateInFlight(contact.entityGuid, false);
  }
};

/** Adapter for el-checkbox @change which emits CheckboxValueType, not boolean. */
const onContactCheckboxChange = (
  contact: ContactItem,
  value: CheckboxValueType,
) => {
  if (typeof value !== "boolean") return;
  return toggleContact(contact, value);
};

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

/** Reload whenever the workflow or primary contact changes. */
watch(
  () => [workflowGuid.value, props.primaryContactGuid],
  () => {
    loadAssociations();
  },
  { immediate: true },
);
</script>

<template>
  <div class="HousingContactActivityAssociations">
    <div class="association-column">
      <div class="association-header">Applications</div>
      <div v-if="!hasWorkflow" class="association-empty">
        Workflow not loaded.
      </div>
      <div v-else-if="loading">
        <el-skeleton :rows="2" animated />
      </div>
      <div v-else-if="loadError" class="association-empty">
        {{ loadError }}
      </div>
      <div v-else-if="application" class="association-item">
        {{ application.entityName }}
      </div>
      <div v-else class="association-empty">No linked application.</div>
    </div>

    <div class="association-column">
      <div class="association-header">Contacts</div>
      <div v-if="!hasWorkflow" class="association-empty">
        Workflow not loaded.
      </div>
      <div v-else-if="loading">
        <el-skeleton :rows="4" animated />
      </div>
      <div v-else-if="loadError" class="association-empty">
        {{ loadError }}
      </div>
      <div v-else-if="contacts.length === 0" class="association-empty">
        No contacts linked to this application.
      </div>
      <div v-else class="association-list">
        <div
          v-for="contact in contacts"
          :key="contact.entityGuid"
          class="association-item"
        >
          <el-checkbox
            :model-value="isContactChecked(contact)"
            :disabled="isContactDisabled(contact)"
            @change="
              (value: CheckboxValueType) =>
                onContactCheckboxChange(contact, value)
            "
          >
            {{ contact.name }}
          </el-checkbox>
          <el-tag v-if="contact.isPrimary" size="small" type="info">
            Primary
          </el-tag>
        </div>
      </div>
    </div>

    <div class="association-column">
      <div class="association-header">Organizations</div>
      <div class="association-empty">Coming soon.</div>
    </div>
  </div>
</template>

<style lang="less">
.HousingContactActivityAssociations {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  .association-column {
    border: 1px solid var(--el-border-color);
    border-radius: var(--el-border-radius-base);
    padding: 0.75rem;
    background-color: var(--el-fill-color-light);
    min-height: 180px;
  }

  .association-header {
    font-weight: 600;
    margin-bottom: 0.75rem;
  }

  .association-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .association-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .association-empty {
    color: var(--el-text-color-secondary);
    font-style: italic;
  }
}
</style>
